import { createFileRoute, Link } from "@tanstack/react-router";
import { MarketingPage, Section } from "@/components/marketing-page";
import { comparisonHead } from "@/lib/seo";

const PATH = "/vs";
const TITLE = "AI Visibility Tool Alternatives & Comparisons | monroya.ai";
const DESCRIPTION =
  "Compare monroya.ai with Otterly, Profound, Qwairy, Peec AI and AthenaHQ side by side. Free plan, paid plans from $79/month.";

type Vs = { name: string; to: string; pitch: string; bestFor: string };

const VS_PAGES: Vs[] = [
  {
    name: "AI competitor analysis tools, compared",
    to: "/vs/ai-competitor-analysis",
    pitch:
      "The whole category in one view, legacy SEO tools vs brand monitoring vs enterprise AEO vs operator AI visibility. The five questions that actually separate them.",
    bestFor: "If you're scoping the category before picking a vendor.",
  },
  {
    name: "Monroya vs Profound",
    to: "/vs/profound",
    pitch:
      "Profound is the enterprise AEO platform; Monroya is the operator tool. Compare on price, time-to-value, and what comes out the other end.",
    bestFor: "If Profound is on your shortlist via procurement.",
  },
  {
    name: "Monroya vs Otterly",
    to: "/vs/otterly",
    pitch:
      "Otterly is a polished tracker. Monroya is an action queue. The decision is whether your team needs the report or the work.",
    bestFor: "If you're weighing reporting depth against execution.",
  },
  {
    name: "Monroya vs AthenaHQ",
    to: "/vs/athenahq",
    pitch:
      "AthenaHQ goes wide on assistants and runs on credits. Monroya measures six core AI surfaces at a flat rate. Match the choice to where your buyers are.",
    bestFor: "If your buyers span the long tail of AI assistants.",
  },
  {
    name: "Monroya vs Qwairy",
    to: "/vs/qwairy",
    pitch:
      "Qwairy runs on monthly credits across engines, markets and languages. Monroya uses flat plans and turns each gap into a ranked job with a draft.",
    bestFor: "If you want a Qwairy alternative with a fixed monthly price.",
  },
  {
    name: "Monroya vs Peec AI",
    to: "/vs/peec-ai",
    pitch:
      "Peec AI is AI search analytics for marketing teams and agencies. Monroya adds a weekly list of fixes and the first draft for each one.",
    bestFor: "If you need to act on the numbers, not only report them.",
  },
  {
    name: "Monroya vs Scrunch AI",
    to: "/vs/scrunch-ai",
    pitch:
      "Scrunch AI starts at $250 a month with a wider engine set on Enterprise. Monroya includes six AI surfaces on every paid plan from $79.",
    bestFor: "If you want a Scrunch AI alternative at a smaller-team price.",
  },
];

export const Route = createFileRoute("/vs/")({
  head: () =>
    comparisonHead({
      competitors: ["Profound", "Otterly", "AthenaHQ", "Qwairy", "Peec AI"],
      path: PATH,
      breadcrumbLabel: "Comparisons",
      breadcrumbParent: { name: "Comparisons", path: "/vs" },
      title: TITLE,
      description: DESCRIPTION,
    }),
  component: VsIndexPage,
});

function VsIndexPage() {
  return (
    <MarketingPage
      stamp="Comparisons"
      title="Compare Monroya with other AI visibility tools"
      dek="Head-to-head pages plus a buyer's guide. Pick the one closest to who you're already evaluating."
      updated="October 2026"
      related={[
        { label: "The full buyer's guide", to: "/best-ai-visibility-tools" },
        { label: "How Monroya's methodology works", to: "/methodology" },
      ]}
    >
      <Section heading="Head-to-head pages">
        <div className="space-y-4">
          {VS_PAGES.map((v) => (
            <Link
              key={v.to}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              to={v.to as unknown as never}
              className="block rounded-lg border border-border bg-paper p-5 transition-colors hover:border-ink"
            >
              <h3 className="font-serif text-2xl text-ink">{v.name}</h3>
              <p className="mt-2 text-ink/85">{v.pitch}</p>
              <p className="mt-3 text-sm text-muted-foreground">
                <span className="font-mono text-[11px] uppercase tracking-wider">
                  Read if
                </span>{" "}
                - {v.bestFor}
              </p>
            </Link>
          ))}
        </div>
      </Section>

      <Section heading="Not sure where to start?">
        <p>
          The{" "}
          <Link
            to="/best-ai-visibility-tools"
            className="font-medium text-ink underline decoration-border underline-offset-4 hover:decoration-ink"
          >
            full buyer's guide
          </Link>{" "}
          summarizes all four tools in one place with the three questions
          that actually separate them, self-serve vs sales-led, dashboard
          vs recommendation, breadth vs depth on the assistant set.
        </p>
      </Section>
    </MarketingPage>
  );
}
